/**
 * Utilitários para trabalhar com subdomínios
 */

/**
 * Verifica se está rodando no GitHub Codespaces
 * @returns {boolean}
 */
export const isCodespaces = () => {
  return window.location.hostname.endsWith('.app.github.dev');
};

/**
 * Obtém a loja selecionada no Codespaces (via localStorage)
 * @returns {string|null}
 */
export const getCodespacesStore = () => {
  if (!isCodespaces()) return null;
  return localStorage.getItem('aliado_codespaces_store') || null;
};

/**
 * Define a loja no Codespaces (salva no localStorage)
 * @param {string} store - Subdomínio da loja
 */
export const setCodespacesStore = (store) => {
  if (store) {
    localStorage.setItem('aliado_codespaces_store', store);
  } else {
    localStorage.removeItem('aliado_codespaces_store');
  }
};

// Sufixos de ambiente reconhecidos no primeiro label do host. Apenas o strip e
// compartilhado: isQaEnvironment() segue exclusivo de "-qa", porque homologacao
// herda o comportamento de producao, nao o de QA.
const ENV_SUFFIXES = ['-qa', '-hmg'];

/**
 * Remove o sufixo de ambiente ("-qa", "-hmg") de um subdomínio, se presente.
 * O sufixo "-qa" indica somente que a API a ser chamada deve ser api-qa.alia.do;
 * em todo o resto do código o subdomínio é tratado SEM o sufixo.
 * @param {string|null} sub
 * @returns {string|null}
 */
export const stripQaSuffix = (sub) => {
  if (!sub) return sub;
  for (const suffix of ENV_SUFFIXES) {
    if (sub.endsWith(suffix)) return sub.slice(0, -suffix.length);
  }
  return sub;
};

/**
 * Indica se o host atual usa o sufixo "-qa" (ambiente QA).
 * @returns {boolean}
 */
export const isQaEnvironment = () => {
  if (typeof window === 'undefined') return false;
  if (isCodespaces()) {
    const store = getCodespacesStore();
    return !!store && store.endsWith('-qa');
  }
  const host = window.location.hostname;
  const parts = host.split('.');
  if (parts.length > 2) {
    return parts[0].endsWith('-qa');
  }
  return false;
};

/**
 * Primeiro label do host, COM o sufixo de ambiente ainda colado.
 * Ex.: "admin-hmg.alia.do" -> "admin-hmg". No Codespaces, vem do localStorage.
 * @returns {string|null}
 */
export const getRawSubdomain = () => {
  if (typeof window === 'undefined') return null;
  if (isCodespaces()) return getCodespacesStore();

  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return null;

  const parts = host.split('.');
  return parts.length > 2 ? parts[0] : null;
};

/**
 * Ambiente em que a interface está rodando, derivado do sufixo do host.
 *
 * Só serve para sinalização visual (a tarja do Header). NÃO usar para liberar
 * feature: as travas seguem em isQaEnvironment(), exclusiva de "-qa", porque
 * homologação herda o comportamento de produção e não o de QA.
 *
 * @returns {'prod'|'qa'|'hmg'}
 */
export const resolveEnv = () => {
  const raw = getRawSubdomain();
  if (!raw) return 'prod';
  if (raw.endsWith('-qa')) return 'qa';
  if (raw.endsWith('-hmg')) return 'hmg';
  return 'prod';
};

/**
 * Extrai o subdomínio da URL atual, removendo o sufixo "-qa".
 * @returns {string|null} Subdomínio "limpo" (sem -qa) ou null
 */
export const extractSubdomain = () => {
  // No Codespaces, usa localStorage como fonte do subdomínio
  if (isCodespaces()) {
    return stripQaSuffix(getCodespacesStore());
  }

  const host = window.location.hostname;

  // Para desenvolvimento local
  if (host === 'localhost' || host === '127.0.0.1') {
    return null;
  }

  // Divide o host por pontos
  const parts = host.split('.');

  // Se tem mais de 2 partes, a primeira é o subdomínio
  if (parts.length > 2) {
    return stripQaSuffix(parts[0]);
  }

  return null;
};

/**
 * Obtém o nome da loja baseado no subdomínio
 * @param {Object} stores Configuração de lojas
 * @returns {string} Nome da loja
 */
export const getStoreName = (stores = {}) => {
  const subdomain = extractSubdomain();

  if (subdomain && stores[subdomain]) {
    return stores[subdomain];
  }

  return stores.default || 'Aliado';
};

/**
 * Gera o bucket name baseado no subdomínio
 * @returns {string} Nome do bucket
 */
export const getBucketName = () => {
  const subdomain = extractSubdomain();
  return subdomain || 'default';
};

/**
 * Obtém o bucket name da autenticação (localStorage)
 * @returns {string} Nome do bucket da autenticação
 */
export const getAuthBucketName = () => {
  try {
    const authData = localStorage.getItem('aliado_auth');
    if (authData) {
      const parsed = JSON.parse(authData);
      if (parsed.user && parsed.user.stores && parsed.user.stores.length > 0) {
        return parsed.user.stores[0].bucket_name;
      }
    }
  } catch (error) {
    console.error('Erro ao extrair bucket_name da autenticação:', error);
  }
  // Fallback para o subdomínio + sufixo
  const subdomain = extractSubdomain();
  return subdomain ? `${subdomain}-aliado` : 'default-aliado';
};

/**
 * Verifica se está em desenvolvimento local
 * @returns {boolean}
 */
export const isLocalDevelopment = () => {
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
};

/**
 * Gera URL com subdomínio
 * @param {string} subdomain 
 * @param {string} path 
 * @returns {string}
 */
export const generateSubdomainUrl = (subdomain, path = '') => {
  if (isLocalDevelopment()) {
    return `http://${subdomain}.localhost:3000${path}`;
  }

  const domain = window.location.hostname.split('.').slice(1).join('.');
  return `https://${subdomain}.${domain}${path}`;
};